// Laying out a pod's route as a graph.
//
// A pod's route is not a line. Odoo records real dependencies between work
// orders (`blocked_by_workorder_ids`), and on a real pod the door and jamb
// subassembly runs alongside the body and merges back in later. Drawing that as
// a list would imply an order that does not exist, and would hide the fact that
// several unrelated steps are outstanding at once.
//
// So both graphs here walk the dependency edges and assign lanes the way a
// commit graph does — reusing a lane where an edge continues straight down, and
// branching into a fresh one where it does not. Both read the way the work
// happens: the first operation at the top, time running down the screen, so a
// row's blockers are always somewhere above it.
//
// `buildRouteGraph` draws the whole route, finished work included.
// `buildUpstreamGraph` keeps only the unfinished work standing between the
// operator and the pod, and hangs it above the operator's own station.
//
// Kept free of React so the shape can be pinned down by tests rather than by
// squinting at a phone.

import type { JourneyStepVM } from '@/api/types';

const isSettled = (s: JourneyStepVM) => s.state === 'done' || s.state === 'cancel';

/** The row below the graph — the operator's own station, drawn by the sheet. */
export const FOOT_ROW = -1;

/** A line from one row to a row above it. */
export interface GraphEdge {
  /** Lane the line arrives in. */
  lane: number;
  /** Row it arrives at. */
  row: number;
}

export interface GraphNode {
  step: JourneyStepVM;
  /**
   * Upstream: steps away from the operator's own, so 1 is an immediate
   * blocker. Route: the row's own index, since every step is drawn.
   */
  depth: number;
  /** Column, 0 is leftmost. */
  lane: number;
  /** The rows above that this one waits on, drawn as lines up to them. */
  edgesUp: GraphEdge[];
}

export interface UpstreamGraph {
  /** Top row first — the earliest operation drawn. */
  nodes: GraphNode[];
  /** How many lanes the drawing needs. */
  lanes: number;
  /** Rows drawn. */
  total: number;
  /**
   * Rows the foot marker joins up to: the last work in the graph, which is
   * what the operator's own station is waiting on. Empty for the full route,
   * which has nothing below its last operation to point at.
   */
  footEdges: GraphEdge[];
}

/** A step's blockers, deduplicated and restricted to the ones being drawn. */
const blockersIn = (step: JourneyStepVM, drawn: Set<number>): number[] =>
  [...new Set(step.blockedBy ?? [])].filter((id) => drawn.has(id));

/**
 * Steps in drawing order, top to bottom.
 *
 * A step is placed only once everything it waits on is already above it, which
 * is what makes reading down the screen the same as reading forwards through
 * the route. Ties go to whichever step comes first along the route, so parallel
 * branches read in route order rather than in whatever order Odoo returned
 * them.
 */
function inDrawOrder(steps: JourneyStepVM[]): JourneyStepVM[] {
  const present = new Set(steps.map((s) => s.woId));
  // How many blockers are still waiting to be drawn. A step can be placed only
  // once that reaches zero, which is what puts every blocker above the work it
  // blocks.
  const outstanding = new Map(steps.map((s) => [s.woId, blockersIn(s, present).length]));
  const unblocks = new Map<number, number[]>();
  for (const s of steps) {
    for (const b of blockersIn(s, present)) unblocks.set(b, [...(unblocks.get(b) ?? []), s.woId]);
  }

  const earliest = (list: JourneyStepVM[], eligible: (s: JourneyStepVM) => boolean): number => {
    let pick = -1;
    for (let i = 0; i < list.length; i++) {
      if (!eligible(list[i]!)) continue;
      if (pick < 0 || list[i]!.sequence < list[pick]!.sequence) pick = i;
    }
    return pick;
  };

  const remaining = [...steps];
  const order: JourneyStepVM[] = [];
  while (remaining.length) {
    let at = earliest(remaining, (s) => outstanding.get(s.woId) === 0);
    // A route that depends on itself — Odoo does not forbid one — would leave
    // nothing eligible and spin here. Take the earliest step and carry on: a
    // drawing with one edge missing beats no drawing at all.
    if (at < 0) at = earliest(remaining, () => true);
    const [step] = remaining.splice(at, 1);
    order.push(step!);
    for (const id of unblocks.get(step!.woId) ?? []) outstanding.set(id, outstanding.get(id)! - 1);
  }
  return order;
}

/**
 * Everything unfinished that stands between this work order and the operator.
 *
 * Traversal continues *through* finished steps: Interpod allows out-of-sequence
 * completion, so a done step can still sit in front of an unfinished one, and
 * stopping at the first completed node would hide the real blocker.
 */
export function buildUpstreamGraph(steps: JourneyStepVM[], fromWoId: number): UpstreamGraph {
  const byId = new Map(steps.map((s) => [s.woId, s]));
  if (!byId.has(fromWoId)) return { nodes: [], lanes: 0, total: 0, footEdges: [] };

  // Breadth-first up the dependency edges. Depth is the shortest hop count, so
  // "nearest" means nearest along the route, not merely earliest in sequence.
  const depth = new Map<number, number>();
  const queue: Array<{ id: number; d: number }> = [{ id: fromWoId, d: 0 }];
  const seen = new Set<number>([fromWoId]);
  while (queue.length) {
    const { id, d } = queue.shift()!;
    for (const parentId of byId.get(id)?.blockedBy ?? []) {
      if (!byId.has(parentId)) continue;
      if (!depth.has(parentId) || d + 1 < depth.get(parentId)!) depth.set(parentId, d + 1);
      if (!seen.has(parentId)) {
        seen.add(parentId);
        queue.push({ id: parentId, d: d + 1 });
      }
    }
  }

  const outstanding = [...depth.entries()]
    .map(([id, d]) => ({ step: byId.get(id)!, depth: d }))
    .filter((n) => !isSettled(n.step));
  const hops = new Map(outstanding.map((n) => [n.step.woId, n.depth]));

  return layout(
    inDrawOrder(outstanding.map((n) => n.step)).map((step) => ({ step, depth: hops.get(step.woId)! })),
    { foot: true },
  );
}

/**
 * The pod's whole route, finished work and all.
 *
 * Ordered so a step is drawn only once everything it waits on is already above
 * it — the first operation at the top, the end of the route at the bottom.
 */
export function buildRouteGraph(steps: JourneyStepVM[]): UpstreamGraph {
  return layout(
    inDrawOrder(steps).map((step, i) => ({ step, depth: i })),
    { foot: false },
  );
}

/**
 * Ordered nodes → lanes and edges, as a commit graph does it: walking from the
 * top row down, a node takes the lane already reserved for it by a row above,
 * otherwise the first free one. The rows that wait on it then reserve lanes in
 * turn — the first inherits this node's lane so a straight chain stays in a
 * column, and each additional one branches into a new lane.
 *
 * `foot` marks the rows nothing in the graph waits on, so the sheet can join
 * them to the operator's own station drawn beneath. The full route has no foot:
 * the only row with nothing under it is the end of the route.
 */
function layout(
  order: Array<{ step: JourneyStepVM; depth: number }>,
  { foot }: { foot: boolean },
): UpstreamGraph {
  const drawn = new Set(order.map((n) => n.step.woId));
  // Which rows wait on each step, in drawing order — the lanes it has to hold
  // open below itself.
  const unblocks = new Map<number, number[]>();
  for (const { step } of order) {
    for (const b of blockersIn(step, drawn)) unblocks.set(b, [...(unblocks.get(b) ?? []), step.woId]);
  }

  const reserved: Array<number | null> = [];
  const nodes: GraphNode[] = [];
  const laneOf = new Map<number, number>();
  const rowOf = new Map<number, number>();

  const claim = (woId: number): number => {
    const existing = reserved.findIndex((r) => r === woId);
    if (existing >= 0) return existing;
    const free = reserved.findIndex((r) => r === null);
    if (free >= 0) {
      reserved[free] = woId;
      return free;
    }
    reserved.push(woId);
    return reserved.length - 1;
  };

  for (const { step, depth: d } of order) {
    const lane = claim(step.woId);
    // Every lane held for this step is released, not just the one it took.
    // Eight flowline steps all wait on the same pod-standing step, so eight
    // lanes were reserved for it; freeing one left seven held for a row that
    // would never come, and the graph crept out to eleven lanes on a route
    // that never runs more than eight things at once.
    for (let i = 0; i < reserved.length; i++) if (reserved[i] === step.woId) reserved[i] = null;
    laneOf.set(step.woId, lane);
    rowOf.set(step.woId, nodes.length);

    // The blockers this row draws up to. The ordering guarantees they are all
    // already placed, except across a cycle — that edge is left undrawn rather
    // than pointed at a row that does not exist yet.
    const edgesUp: GraphEdge[] = blockersIn(step, drawn)
      .filter((id) => rowOf.has(id))
      .map((id) => ({ lane: laneOf.get(id)!, row: rowOf.get(id)! }));

    (unblocks.get(step.woId) ?? []).forEach((id, i) => {
      if (i === 0) reserved[lane] = id;
      else claim(id);
    });

    nodes.push({ step, depth: d, lane, edgesUp });
  }

  return {
    nodes,
    lanes: nodes.reduce((max, n) => Math.max(max, n.lane + 1, ...n.edgesUp.map((e) => e.lane + 1)), 1),
    total: nodes.length,
    footEdges: foot
      ? nodes
          .filter((n) => (unblocks.get(n.step.woId) ?? []).length === 0)
          .map((n) => ({ lane: n.lane, row: rowOf.get(n.step.woId)! }))
      : [],
  };
}
